import { UploadFileInfo } from '@progress/kendo-react-upload';
import { fileTypeFromBuffer } from 'file-type';
import mime from 'mime';
import { PDFDocument } from 'pdf-lib';
import { toast } from 'react-toastify';

export type PdfInspectError = 'password-protected' | 'corrupt';

export interface PdfInspectResult {
  pageCount: number;
}

export interface PdfFileMeta {
  isLoading: boolean;
  pageCount: number | null;
  error: PdfInspectError | null;
  draftRange: string;
  confirmedRange: string | null;
  validationError: PageRangeValidationError;
  isPanelOpen: boolean;
}

export type PageRangeValidationError =
  | { key: 'scanByPage.invalidFormat' }
  | { key: 'scanByPage.invalidPageNumber'; page: string; total: number }
  | { key: 'scanByPage.rangeInverted' }
  | { key: 'scanByPage.pageExceedsCount'; input: string; total: number }
  | { key: 'scanByPage.duplicatePageNumber'; page: string }
  | null;

const PAGE_RANGE_SEGMENT = /^(\d+)(?:-(\d+))?$/;

const PAGE_RANGE_INPUT_PATTERN = /[^\d,-]/g;

export const sanitizePageRangeInput = (value: string): string =>
  value.replace(PAGE_RANGE_INPUT_PATTERN, '');

/** True when the draft/confirmed page-range field contains at least one digit. */
export const hasPageRangeDigits = (value: string): boolean => /\d/.test(value);

const isSingleNumberInput = (value: string): boolean => /^\d+$/.test(value);

const invalidPageNumber = (page: string, totalPages: number): PageRangeValidationError => ({
  key: 'scanByPage.invalidPageNumber',
  page,
  total: totalPages,
});

export const createInitialPdfFileMeta = (): PdfFileMeta => ({
  isLoading: false,
  pageCount: null,
  error: null,
  draftRange: '',
  confirmedRange: null,
  validationError: null,
  isPanelOpen: false,
});

export const inspectPdf = async (file: File): Promise<PdfInspectResult> => {
  const buffer = await file.arrayBuffer();
  try {
    const pdf = await PDFDocument.load(buffer);
    return { pageCount: pdf.getPageCount() };
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message.toLowerCase() : '';
    if (message.includes('encrypt') || message.includes('password')) {
      throw new Error('password-protected');
    }
    throw new Error('corrupt');
  }
};

export const validatePageRange = (input: string, totalPages: number): PageRangeValidationError => {
  const trimmed = input.trim();
  if (!trimmed) {
    return { key: 'scanByPage.invalidFormat' };
  }

  if (isSingleNumberInput(trimmed)) {
    const page = Number(trimmed);
    if (page < 1 || page > totalPages) {
      return invalidPageNumber(trimmed, totalPages);
    }
    return null;
  }

  const segments = trimmed
    .split(',')
    .map((segment) => segment.trim())
    .filter(Boolean);

  if (segments.length === 0) {
    return { key: 'scanByPage.invalidFormat' };
  }

  const seenSinglePages = new Set<number>();

  for (const segment of segments) {
    const match = segment.match(PAGE_RANGE_SEGMENT);
    if (!match) {
      return { key: 'scanByPage.invalidFormat' };
    }

    const start = Number(match[1]);
    const end = match[2] != null ? Number(match[2]) : start;

    if (start < 1 || end < 1) {
      return invalidPageNumber(String(start < 1 ? start : end), totalPages);
    }
    if (start > end) {
      return { key: 'scanByPage.rangeInverted' };
    }
    if (start > totalPages || end > totalPages) {
      return {
        key: 'scanByPage.pageExceedsCount',
        input: trimmed,
        total: totalPages,
      };
    }

    // A range written with identical start and end (e.g. "1-1") redundantly
    // references the same page twice - treat it the same as a duplicate.
    if (match[2] != null && start === end) {
      return { key: 'scanByPage.duplicatePageNumber', page: String(start) };
    }

    // Only single-page entries (not ranges) are checked for duplicates.
    if (match[2] == null) {
      if (seenSinglePages.has(start)) {
        return { key: 'scanByPage.duplicatePageNumber', page: String(start) };
      }
      seenSinglePages.add(start);
    }
  }

  return null;
};

export const getFileTypeFromContent = async (file: File) => {
  if (file) {
    const buffer = await file.arrayBuffer();
    const fileType = await fileTypeFromBuffer(buffer);
    return fileType;
  }
};

const imageUploadExtensions = ['.jpg', '.jpeg', '.gif', '.bmp', '.png'] as const;

const getDeclaredExtension = (file: UploadFileInfo): string => file.extension?.toLowerCase() || '';

const matchesDeclaredExtensionMime = (detectedMime: string, declaredExtension: string): boolean => {
  const mimeType = mime.getType(declaredExtension);
  return !!mimeType && mimeType === detectedMime;
};

export const matchesContentFileType = async (
  file: UploadFileInfo,
  allowedExtensions: readonly string[]
): Promise<boolean> => {
  const rawFile = file.getRawFile?.() as File | undefined;
  if (!rawFile) {
    return false;
  }
  const fileType = await getFileTypeFromContent(rawFile);
  if (!fileType?.ext || !fileType.mime) {
    return false;
  }
  const declaredExtension = getDeclaredExtension(file);
  return (
    allowedExtensions.includes(`.${fileType.ext}`) &&
    matchesDeclaredExtensionMime(fileType.mime, declaredExtension)
  );
};

export const isPdfFile = async (file: UploadFileInfo): Promise<boolean> =>
  matchesContentFileType(file, ['.pdf']);

export const isImageUploadFile = async (file: UploadFileInfo): Promise<boolean> =>
  matchesContentFileType(file, imageUploadExtensions);

export const isPreviewableUploadFile = async (file: UploadFileInfo): Promise<boolean> =>
  (await isPdfFile(file)) || (await isImageUploadFile(file));

export const filterPreviewableUploadFiles = async (
  files: UploadFileInfo[]
): Promise<UploadFileInfo[]> => {
  const results = await Promise.all(
    files.map(async (file) => ({
      file,
      previewable: await isPreviewableUploadFile(file),
    }))
  );
  return results.filter((result) => result.previewable).map((result) => result.file);
};

export interface unProcessedFiles {
  fileName: string;
  errorMessage: string;
  file?: UploadFileInfo;
}
export interface PreRequiredCheck {
  processed: UploadFileInfo[];
  unProcessed: unProcessedFiles[];
}

export interface ProcessedFilesState {
  processedFiles: UploadFileInfo[];
  preRequiredCheck: PreRequiredCheck;
}

export const allowedOcrFileExtensions = ['.pdf', '.jpg', '.jpeg', '.png'];
export const allowedExtensions = ['.pdf', '.jpg', '.jpeg', '.gif', '.bmp', '.png'];

export const S3FileValidations = async (
  affectedFiles: UploadFileInfo[],
  uploadPdfOnly: boolean
): Promise<ProcessedFilesState> => {
  const processedFiles: UploadFileInfo[] = [];
  const preRequiredCheck: PreRequiredCheck = {
    processed: [],
    unProcessed: [],
  };
  try {
    for (let i = 0; i < affectedFiles.length; i++) {
      const file = affectedFiles[i];
      let ext = file.extension?.toLocaleLowerCase() || '';
      const isPDF = ext === '.pdf';
      const isBelow5MB = typeof file.size === 'number' ? file.size <= 5 * 1024 * 1024 : true;
      const rawFile = file.getRawFile?.() as File;
      const fileType = await getFileTypeFromContent(rawFile);
      const mimeType = mime.getType(ext);
      const isValidExtension = fileType
        ? allowedExtensions.includes('.' + fileType.ext) && mimeType === fileType.mime
        : false;

      if (uploadPdfOnly && !isPDF) {
        preRequiredCheck.unProcessed.push({
          fileName: file.name,
          errorMessage: 'File format is not supported or does not match the extension.',
          file,
        });
      } else if (!isBelow5MB) {
        preRequiredCheck.unProcessed.push({
          fileName: file.name,
          errorMessage: 'File size is above 5 MB.',
          file,
        });
      } else if (!isValidExtension) {
        preRequiredCheck.unProcessed.push({
          fileName: file.name,
          errorMessage: 'File format is not supported or file extension renamed improperly.',
          file,
        });
      } else {
        processedFiles.push(file);
        preRequiredCheck.processed.push(file);
      }
    }
  } catch (error) {
    toast.error('Something went wrong');
    console.error(error);
  }

  return {
    processedFiles,
    preRequiredCheck,
  };
};
